import { useId } from 'react';
import { cn } from '@/lib/utils';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { LICENSES, OPENNESS_TINT } from '../utils/licenseInfo';

interface LicenseSelectorProps {
  selected: string;
  onSelect: (code: string) => void;
}

export default function LicenseSelector({
  selected,
  onSelect,
}: LicenseSelectorProps) {
  const uid = useId();
  return (
    <RadioGroup
      value={selected}
      onValueChange={onSelect}
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
    >
      {LICENSES.map(({ code, name, desc, openness }) => (
        <label
          key={code}
          htmlFor={`${uid}-${code}`}
          className={cn(
            'flex cursor-pointer flex-col rounded-xl border p-4 transition-[background-color,border-color,box-shadow] duration-fast',
            selected === code
              ? 'border-primary bg-accent shadow-[0_0_0_1px] shadow-primary'
              : 'border-border bg-card hover:bg-muted/40'
          )}
        >
          <div className="flex items-start justify-between gap-2">
            <RadioGroupItem
              id={`${uid}-${code}`}
              value={code}
              className="mt-0.5 border-muted-foreground/40 data-[state=checked]:border-primary"
            />
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[11px] font-medium',
                OPENNESS_TINT[openness]
              )}
            >
              {openness}
            </span>
          </div>
          {/* semibold at 14px closes the gap around the i dot: i reads as l */}
          <div className="mt-3 text-sm font-medium tracking-[0.01em] leading-snug">
            {name}
          </div>
          {/* the compare-table columns are labelled by code, not name */}
          <div className="mt-0.5 font-tertiary text-[11px] text-muted-foreground">
            {code}
          </div>
          <div className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
            {desc}
          </div>
        </label>
      ))}
    </RadioGroup>
  );
}
